import type { Query } from '@tanstack/react-query';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { readStatusCode } from '@/utils/apiErrors';
import { syncCustomerIdFromSession } from '@/utils/syncCustomerIdFromSession';

/**
 * Decides what PersistQueryClientProvider writes to disk: successful queries,
 * unless they opt out with `meta: { persist: false }` (single-use data such as
 * a payment intent must never be hydrated into a later session).
 */
export const shouldPersistQuery = (query: Query): boolean =>
  query.state.status === 'success' && query.meta?.persist !== false;

/**
 * A 403 means the stored customerId isn't the session's own, so it's replaced
 * with the session's; queries keyed on it then refetch (SQR-15987).
 */
const healCustomerIdOnForbidden = (error: unknown) => {
  if (readStatusCode(error) === 403) void syncCustomerIdFromSession();
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: healCustomerIdOnForbidden }),
  mutationCache: new MutationCache({ onError: healCustomerIdOnForbidden }),
  defaultOptions: {
    queries: {
      gcTime: 3 * 60 * 1000, // 3 minutes (300000ms)
      experimental_prefetchInRender: true,
    },
    mutations: {},
  },
});
